import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ModalBody } from 'ui-components/components/modal/modal-body';
import { Wizard } from 'ui-components/components/wizard';
import { WizardStep } from 'ui-components/components/wizard/wizard-step';
import { noop } from 'ui-components/default-props/noop';

import { useGlobalModalLaunchContext } from '../../../../../contexts/modal-context/global-modal-context';
import { TaxFormContextProvider } from '../../../../../pages/settings/payment-and-subscription/context/tax-form-context-provider';
import { PageEventSourceStrings } from '../../../../../pages/settings/payment-and-subscription/hooks/use-track-event';
import { messages } from '../../../../messages';
import { ADD_FUNDS_TAB, PAYMENT_TAB, TERMS_TAB } from '../../../../utilities/constants';
import { ConditionalFsuiContextProvider } from '../../contexts/conditional-fsui-context';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';
import { AddFundsStep } from '../../steps/add-funds-step/';
import { PaymentAndLocationStep } from '../../steps/payment-and-location/';
import { AdyenSimplePickerBillingStep } from '../../steps/payment-and-location/simple-picker';
import { TermsStep } from '../../steps/terms-step/terms-step';
import style from '../../style.module.less';
import { WalletModalFooter } from '../modal-footer';

/**
 * @param {import('react-intl').IntlShape} intl
 * @param {'PAYMENT_TAB' | 'TERMS_TAB' | 'ADD_FUNDS'} stepId
 * @returns {string}
 */
const getLocalizedStepLabel = (intl, stepId) => {
  if (stepId === PAYMENT_TAB) {
    return intl.formatMessage(messages.paymentTab);
  }

  if (stepId === TERMS_TAB) {
    return intl.formatMessage(messages.termsTab);
  }
  if (stepId === ADD_FUNDS_TAB) {
    return intl.formatMessage(messages.addFundsTab);
  }

  return '';
};

/**
 * @param {object} props
 * @param {() => void} [props.setWasTermsStepSuccessful]
 * @returns {React.ReactElement}
 */
export const WalletModalBody = ({ setWasTermsStepSuccessful = noop }) => {
  const intl = useIntl();
  const { isStepRequired, shouldShowAdyenDropInComponent, walletToRegister } = useWalletRegistrationModalContext();
  const { setIsPaymentComplete, setHasSubmittedPayment } = useWalletRegistrationFormContext();
  const { setCurrentWalletRegistrationStepId: setCurrentStepId, currentWalletRegistrationStepId: initialStepId } =
    useGlobalModalLaunchContext();

  const handleSetCurrentStepId = useCallback(
    (stepId) => {
      if (stepId === PAYMENT_TAB) {
        // this marks the payment as no-longer complete, so the page can correctly handle payments again
        setIsPaymentComplete(false);
        setHasSubmittedPayment(false);
      }
      setCurrentStepId(stepId);
    },
    [setCurrentStepId, setIsPaymentComplete, setHasSubmittedPayment],
  );

  const getStepLabel = useCallback(
    (stepId) => {
      return getLocalizedStepLabel(intl, stepId);
    },
    [intl],
  );

  // BTC TODO: when non-adyen routes are blocked, we can remove PaymentAndLocationStep by rendering an empty modal or simply not opening the modal
  const PaymentAndLocationStepComponent = shouldShowAdyenDropInComponent ? <AdyenSimplePickerBillingStep /> : <PaymentAndLocationStep />;

  return (
    <ConditionalFsuiContextProvider currencyCode={walletToRegister?.currencyCode}>
      <TaxFormContextProvider isEditing={false} source={PageEventSourceStrings.WALLET_REGISTRATION}>
        <ModalBody className={style['modal-body']}>
          <Wizard
            footer={<WalletModalFooter setWasTermsStepSuccessful={setWasTermsStepSuccessful} />}
            getStepLabel={getStepLabel}
            hideStepLabels
            initialStepId={initialStepId}
            setCurrentStepId={handleSetCurrentStepId}
          >
            {isStepRequired(PAYMENT_TAB) && <WizardStep stepId={PAYMENT_TAB}>{PaymentAndLocationStepComponent}</WizardStep>}
            {isStepRequired(TERMS_TAB) && (
              <WizardStep stepId={TERMS_TAB}>
                <TermsStep />
              </WizardStep>
            )}
            {isStepRequired(ADD_FUNDS_TAB) && (
              <WizardStep stepId={ADD_FUNDS_TAB}>
                <AddFundsStep />
              </WizardStep>
            )}
          </Wizard>
        </ModalBody>
      </TaxFormContextProvider>
    </ConditionalFsuiContextProvider>
  );
};
